import React, { useMemo, useState, useCallback } from 'react';
import { Trash, Funnel, Plus } from '@phosphor-icons/react';
import { useData } from '../../../hooks/useData';
import { useApp } from '../../../contexts/AppContext';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../utils/errorLogging';
import { TABLES, MODAL_TYPES } from '../../../constants';
import tableStyles from '../../../styles/tables.module.css';
import styles from './BudgetPage.module.css';

/**
 * PaymentsTab - Vendor/Quote-centric payments view
 * Shows all payments across the project, grouped/filterable by vendor
 * Payments are linked to quotes (the "accounts payable" record)
 */
const PaymentsTab = ({ currentProject }) => {
  const { openModal } = useApp();
  const { logError } = useErrorLogging();

  // Filter state
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch all payments for this project via quotes
  // Payment → Quote → Vendor
  const { data: allQuotes, isLoading: quotesLoading } = useData(TABLES.VENDOR_QUOTES, {
    select: `
      id,
      total_price,
      status,
      master_vendor_id,
      master_vendors(id, name)
    `,
    filter: { project_id: currentProject?.id },
    enabled: Boolean(currentProject?.id),
    realtime: true,
  });

  // Get quote IDs for filtering payments
  const quoteIds = useMemo(() => allQuotes?.map(q => q.id) || [], [allQuotes]);

  const {
    data: allPayments,
    isLoading: paymentsLoading,
    update: updatePayment,
    remove: deletePayment,
  } = useData(TABLES.PROJECT_PAYMENTS, {
    select: `
      id,
      quote_id,
      amount,
      due_date,
      payment_date,
      status,
      notes,
      attachments,
      payment_type
    `,
    filter: quoteIds.length > 0 ? { quote_id: quoteIds } : undefined,
    enabled: Boolean(currentProject?.id) && quoteIds.length > 0,
    realtime: true,
  });

  // Create lookup maps
  const quotesMap = useMemo(() => {
    if (!allQuotes) return new Map();
    return new Map(allQuotes.map(q => [q.id, q]));
  }, [allQuotes]);

  // Get unique vendors for filter dropdown
  const vendors = useMemo(() => {
    if (!allQuotes) return [];
    const vendorMap = new Map();
    allQuotes.forEach(q => {
      if (q.master_vendors) {
        vendorMap.set(q.master_vendors.id, q.master_vendors);
      }
    });
    return Array.from(vendorMap.values());
  }, [allQuotes]);

  // Enrich payments with quote/vendor data and apply filters
  const enrichedPayments = useMemo(() => {
    if (!allPayments) return [];

    return allPayments
      .map(payment => {
        const quote = quotesMap.get(payment.quote_id);
        return {
          ...payment,
          quote,
          vendor: quote?.master_vendors,
          vendorName: quote?.master_vendors?.name || 'לא ידוע',
        };
      })
      .filter(payment => {
        // Apply vendor filter
        if (vendorFilter && payment.vendor?.id !== vendorFilter) {
          return false;
        }
        // Apply status filter
        if (statusFilter && payment.status !== statusFilter) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Sort by due date (earliest first), nulls at end
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      });
  }, [allPayments, quotesMap, vendorFilter, statusFilter]);

  // Calculate totals
  const totals = useMemo(() => {
    const pending = enrichedPayments
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    const completed = enrichedPayments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    const total = enrichedPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    return { pending, completed, total };
  }, [enrichedPayments]);

  // Handle status change
  const handleStatusChange = useCallback(
    async (paymentId, newStatus) => {
      try {
        await updatePayment({
          id: paymentId,
          data: {
            status: newStatus,
            payment_date: newStatus === 'completed' ? new Date().toISOString().split('T')[0] : null,
          },
        });
      } catch (error) {
        logError(error, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_MUTATION,
          message: 'Failed to update payment status',
        });
      }
    },
    [updatePayment, logError],
  );

  // Handle delete
  const handleDelete = useCallback(
    async paymentId => {
      if (!window.confirm('האם אתה בטוח שברצונך למחוק תשלום זה?')) return;
      try {
        await deletePayment(paymentId);
      } catch (error) {
        logError(error, {
          severity: ERROR_SEVERITY.ERROR,
          category: ERROR_CATEGORY.DATA_MUTATION,
          message: 'Failed to delete payment',
        });
      }
    },
    [deletePayment, logError],
  );

  // Handler to open quote form for adding new payment
  // IMPORTANT: Must be defined before any early returns to satisfy React hooks rules
  const handleAddPayment = useCallback(() => {
    openModal(MODAL_TYPES.QUOTE_FORM, {
      projectId: currentProject?.id,
    });
  }, [openModal, currentProject?.id]);

  const isLoading = quotesLoading || paymentsLoading;

  if (isLoading) {
    return <div className={styles.loadingContainer}>טוען תשלומים...</div>;
  }

  if (!allQuotes?.length) {
    return (
      <div className={styles.emptyState}>
        <p>אין הצעות מחיר מאושרות</p>
        <p className={styles.emptyStateHint}>תשלומים נוצרים מהצעות מחיר שאושרו</p>
        <button className={styles.addPaymentButton} onClick={handleAddPayment}>
          <Plus size={16} />
          הוסף הצעת מחיר
        </button>
      </div>
    );
  }

  return (
    <div className={styles.paymentsTab}>
      {/* Summary Cards */}
      <div className={styles.paymentsSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>סה״כ לתשלום</span>
          <span className={styles.summaryValue}>₪{totals.pending.toLocaleString()}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>שולם</span>
          <span className={styles.summaryValueSuccess}>₪{totals.completed.toLocaleString()}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.summaryLabel}>סה״כ</span>
          <span className={styles.summaryValue}>₪{totals.total.toLocaleString()}</span>
        </div>
      </div>

      {/* Actions Row */}
      <div className={styles.filtersRow}>
        <button className={styles.addPaymentButton} onClick={handleAddPayment}>
          <Plus size={16} />
          הוסף הצעת מחיר
        </button>
        <button className={styles.filterToggle} onClick={() => setShowFilters(!showFilters)}>
          <Funnel size={16} />
          סינון
        </button>
        {showFilters && (
          <div className={styles.filtersContainer}>
            <select
              className={tableStyles.inlineSelect}
              value={vendorFilter}
              onChange={e => setVendorFilter(e.target.value)}
            >
              <option value="">כל הספקים</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
            <select
              className={tableStyles.inlineSelect}
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">כל הסטטוסים</option>
              <option value="pending">ממתין</option>
              <option value="completed">שולם</option>
              <option value="cancelled">בוטל</option>
            </select>
            {(vendorFilter || statusFilter) && (
              <button
                className={styles.clearFilters}
                onClick={() => {
                  setVendorFilter('');
                  setStatusFilter('');
                }}
              >
                נקה סינון
              </button>
            )}
          </div>
        )}
      </div>

      {/* Payments Table */}
      {enrichedPayments.length === 0 ? (
        <div className={styles.noPayments}>
          {vendorFilter || statusFilter ? 'אין תשלומים התואמים לסינון' : 'אין תשלומים'}
        </div>
      ) : (
        <div className={tableStyles.tableContainer}>
          <div className={tableStyles.tableScrollWrapper}>
            <table className={tableStyles.table}>
              <thead>
                <tr className={tableStyles.headerRow}>
                  <th className={tableStyles.headerCell}>ספק</th>
                  <th className={tableStyles.headerCell}>סכום</th>
                  <th className={tableStyles.headerCell}>תאריך לתשלום</th>
                  <th className={tableStyles.headerCell}>תאריך תשלום</th>
                  <th className={tableStyles.headerCell}>סטטוס</th>
                  <th className={tableStyles.headerCell}>הערות</th>
                  <th className={tableStyles.headerCell}>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {enrichedPayments.map(payment => {
                  const isPastDue =
                    payment.status === 'pending' &&
                    payment.due_date &&
                    new Date(payment.due_date) < new Date();

                  return (
                    <tr key={payment.id} className={tableStyles.row}>
                      <td className={tableStyles.cell}>
                        <span className={styles.vendorName}>{payment.vendorName}</span>
                      </td>
                      <td className={tableStyles.cell}>₪{payment.amount?.toLocaleString() || 0}</td>
                      <td className={`${tableStyles.cell} ${isPastDue ? tableStyles.pastDue : ''}`}>
                        {payment.due_date
                          ? new Date(payment.due_date).toLocaleDateString('he-IL')
                          : '-'}
                        {isPastDue && <div className={styles.pastDueLabel}>באיחור</div>}
                      </td>
                      <td className={tableStyles.cell}>
                        {payment.payment_date
                          ? new Date(payment.payment_date).toLocaleDateString('he-IL')
                          : '-'}
                      </td>
                      <td className={tableStyles.cell}>
                        <select
                          className={tableStyles.inlineSelect}
                          value={payment.status || ''}
                          onChange={e => handleStatusChange(payment.id, e.target.value)}
                        >
                          <option value="pending">ממתין</option>
                          <option value="completed">שולם</option>
                          <option value="cancelled">בוטל</option>
                        </select>
                      </td>
                      <td className={tableStyles.cell}>
                        <span className={styles.notesText}>{payment.notes || '-'}</span>
                      </td>
                      <td className={`${tableStyles.cell} ${tableStyles.actionsCell}`}>
                        <button
                          onClick={() => handleDelete(payment.id)}
                          className={tableStyles.deleteButton}
                          title="מחק תשלום"
                        >
                          <Trash size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsTab;
